import {useEffect, useState} from "react"
import styled, {css} from "styled-components"
import {defaultThemeColors} from "ui/theme";


export function ToggleGroup({
                                options,
                                value,
                                onChange,
                                name = "toggleGroup",
                                size = "medium",
                                variant = "contained",
                                direction = "horizontal",
                                fullWidth = false,
                                disabled = false,
                                className,
                            }) {
    const [selectedValue, setSelectedValue] = useState(value || "")

    useEffect(() => {
        setSelectedValue(value)
    }, [value]);

    const handleChange = (option) => {
        if (disabled) return
        setSelectedValue(option.value)
        onChange?.(option)
    }

    return (
        <ToggleContainer $direction={direction} $fullWidth={fullWidth} $variant={variant} className={className}>
            {options.map((option) => (
                <ToggleButton
                    key={option.value}
                    $isSelected={selectedValue === option.value}
                    $size={size}
                    $variant={variant}
                    $fullWidth={fullWidth}
                    $disabled={disabled || option.disabled || false}
                    onClick={() => !disabled && !option.disabled && handleChange(option)}
                >
                    <HiddenInput
                        type="radio"
                        name={name}
                        value={option.value}
                        checked={selectedValue === option.value}
                        disabled={disabled || option.disabled}
                        onChange={() => handleChange(option)}
                    />
                    {option.label}
                </ToggleButton>
            ))}
        </ToggleContainer>
    )
}


const sizeStyles = {
    small: css`
        padding: 8px 16px;
        font-size: 14px;
        min-width: 80px;
    `,
    medium: css`
        padding: 12px 24px;
        font-size: 16px;
        min-width: 120px;
    `,
    large: css`
        padding: 16px 32px;
        font-size: 18px;
        min-width: 140px;
    `,
}

const variantStyles = {
    contained: css`
        background: ${(props) => (
                props.$isSelected
                        ? defaultThemeColors.blue
                        : defaultThemeColors.transparent
        )};

        color: ${(props) => (
                props.$isSelected
                        ? defaultThemeColors.white
                        : defaultThemeColors.gray
        )};
        border: none;

        &:hover:not(:disabled) {
            background: ${(props) => (
                    props.$isSelected
                            ? defaultThemeColors.blue
                            : defaultThemeColors.lightBlue
            )};
        }
    `,
    outlined: css`
        background: ${(props) => (
                props.$isSelected
                        ? defaultThemeColors.blue
                        : defaultThemeColors.transparent
        )};
        color: ${(props) => (
                props.$isSelected
                        ? defaultThemeColors.white
                        : defaultThemeColors.blue
        )};
        border: 2px solid ${defaultThemeColors.blue};

        &:hover:not(:disabled) {
            background: ${(props) => (
                    props.$isSelected ?
                            defaultThemeColors.blue
                            : defaultThemeColors.blue15
            )};
            border-color: ${defaultThemeColors.blue};
        }
    `,
    minimal: css`
        background: ${(props) => (
                props.$isSelected
                        ? defaultThemeColors.blue15
                        : defaultThemeColors.transparent
        )};
        color: ${(props) => (
                props.$isSelected
                        ? defaultThemeColors.blue
                        : defaultThemeColors.gray
        )};
        border: 1px solid ${(props) => (
                props.$isSelected
                        ? defaultThemeColors.blue
                        : defaultThemeColors.border
        )};

        &:hover:not(:disabled) {
            background: ${(props) => (
                    props.$isSelected
                            ? defaultThemeColors.blue15
                            : defaultThemeColors.lightBlue2
            )};
            border-color: ${defaultThemeColors.blue};
        }
    `,
}

const ToggleContainer = styled.div`
    display: flex;
    flex-direction: ${(props) => (props.$direction === "vertical" ? "column" : "row")};
    gap: ${(props) => (props.$variant === "contained" ? "4px" : "8px")};
    padding: ${(props) => (props.$variant === "contained" ? "4px" : "0")};
    background: ${(props) => (props.$variant === "contained"
                    ? defaultThemeColors.lightBlue2
                    : defaultThemeColors.transparent
    )};
    border-radius: ${(props) => (props.$variant === "contained" ? "12px" : "0")};
    width: ${(props) => (props.$fullWidth ? "100%" : "fit-content")};
`

const ToggleButton = styled.label`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    font-weight: 600;
    cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
    transition: all 0.2s ease;
    text-align: center;
    opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
    flex: ${(props) => (props.$fullWidth ? 1 : "none")};
    white-space: nowrap;

    ${(props) => sizeStyles[props.$size]}
    ${(props) => variantStyles[props.$variant]}
`

const HiddenInput = styled.input`
    position: absolute;
    opacity: 0;
    pointer-events: none;
`